import { useEffect, useState } from "react"
import CloseButton from "../../../components/CloseButton/CloseButton"
import PrimaryButton from "../../../components/PrimaryButton/PrimaryButton"
import { usePromo } from "../../../context/promoContext"
import useQueryParams from "../../../hooks/useQueryParams"
import useStrings from "../../../localization/useStrings"
import native from "../../../native/native"
import Svg from "../../../utils/svg"
import styles from "./TAPromoPaywall.module.scss"

interface TAPromoPaywallProps {
  slideIn?: boolean
  rightAlignClose?: boolean
}
const TAPromoPaywall: React.FC<TAPromoPaywallProps> = ({
  slideIn = false,
  rightAlignClose = false,
}) => {
  const [visible, setVisible] = useState(!slideIn)

  useEffect(() => {
    if (slideIn) {
      const id = requestAnimationFrame(() => setVisible(true))
      return () => cancelAnimationFrame(id)
    }
  }, [slideIn])

  return (
    <div
      className={[
        styles.container,
        slideIn && styles.slide,
        visible && styles.visible,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className={styles.contentWrapper}>
        <Content rightAlignClose={rightAlignClose} />
        <Footer />
      </div>
    </div>
  )
}

const Content = ({ rightAlignClose }: TAPromoPaywallProps) => {
  const strings = useStrings()
  const { state } = usePromo()
  const query = useQueryParams()

  const discountedPrice =
    " " +
    strings.multistep
      .yearlyPrice(state.pricing.yearlyPriceDiscounted.formatted)
      .toLowerCase()

  return (
    <div className={styles.content}>
      <label className={styles.title}>{strings.oneTimeOfferTitle}</label>
      {!query.alternateClose && (
        <CloseButton
          className={
            rightAlignClose ? styles.closeButtonRight : styles.closeButton
          }
          onClick={() => {
            native.postNativeEvent("close")
          }}
        />
      )}
      <DiscountCoupon />
      <label className={styles.subtitle}>{strings.unlockPremiumFor}</label>
      <label className={styles.price}>
        <label className={styles.strikeThrough}>
          {state.pricing.yearlyPriceFull.formatted}
        </label>
        {discountedPrice}
      </label>
      <label className={styles.oneTimeOffer}>
        {strings.oneTimeOfferSubtitle}
      </label>
    </div>
  )
}

const DiscountCoupon = () => {
  const { state } = usePromo()
  const strings = useStrings()

  return (
    <div className={styles.discountCoupon}>
      <Svg.StartsLeft />
      <div className={styles.discountContainer}>
        {strings.discount(Math.round(state.pricing.savingPercent))}
      </div>
      <Svg.StartsRight />
    </div>
  )
}

const Footer = () => {
  const strings = useStrings()
  const { actions } = usePromo()
  const query = useQueryParams()

  return (
    <div className={styles.footer}>
      <OfferBox />
      <PrimaryButton
        className={styles.cta}
        onClick={() => {
          actions.subscribe()
        }}
      >
        {strings.promo.claimOffer.toUpperCase()}
      </PrimaryButton>
      {query.alternateClose ? (
        <button
          className={styles.cancelAnytime}
          onClick={() => {
            native.postNativeEvent("close")
          }}
        >
          {strings.beforeYouGo.skipCta}
        </button>
      ) : (
        <label className={styles.cancelAnytime}>{strings.cancelAnytime}</label>
      )}
    </div>
  )
}

const OfferBox = () => {
  const strings = useStrings()
  const { state } = usePromo()

  const totalPrice =
    strings.premiumToUltimateBlackFriday.monthPrefix +
    " " +
    state.pricing.yearlyPriceDiscounted.formatted

  return (
    <div className={styles.planContainer}>
      <div className={styles.planHeader}>{strings.lowestPrice}</div>

      <div className={styles.planContent}>
        <div className={styles.planLeft}>
          <label className={styles.planTitle}>{strings.yearlyPlan}</label>
          <label className={styles.planPriceDetail}>{totalPrice}</label>
        </div>
        <div className={styles.planRight}>
          {strings.monthlyPrice(state.pricing.discountedMonthlyPrice.formatted)}
        </div>
      </div>
    </div>
  )
}

export default TAPromoPaywall
